import { useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import { buildSpeedSegments, getSpeedColor } from '@/features/replay/replayUtils';
import { whenStyleReady } from '@/lib/mapStyleReady';
import useReplayTrackWidth from '@/hooks/useReplayTrackWidth';
import { animateLineDash } from '@/lib/replayFlowDash';

const THRESHOLDS = { slow: 60, moderate: 90 };

// Flow dashes ride slightly thinner than the backdrop so the speed-colored
// route stays visible as a continuous line underneath.
const flowWidthFor = (w) => Math.max(2, w - 2);

/**
 * Side-effect-only component that manages MapLibre sources, layers, and DOM
 * markers for mini replay on the Live Map. Renders null.
 *
 * Props:
 *   mapRef        — React ref to MapView component (call mapRef.current.getMap())
 *   positions     — Array of Traccar position objects, or null
 *   progress      — Float 0-1 representing playback progress
 *   currentPosition — Interpolated position { latitude, longitude, speed, course } from useReplayEngine
 *   fitRouteRef   — Mutable ref; this component assigns fitRouteRef.current = () => map.fitBounds(...)
 */
export default function MiniReplayLayer({ mapRef, positions, progress, currentPosition, fitRouteRef, follow = false }) {
  const ghostMarkerRef = useRef(null);
  const startMarkerRef = useRef(null);
  const endMarkerRef = useRef(null);
  const dashAnimRef = useRef(null);
  const { width: trackWidth } = useReplayTrackWidth();

  // Effect 1: Set up route sources, layers and DOM markers when positions change
  useEffect(() => {
    const map = mapRef.current?.getMap?.();
    if (!map || !positions || positions.length < 2) return;

    const setup = () => {
      // Build full speed-colored segments
      const fullSegments = buildSpeedSegments(positions, THRESHOLDS);

      // Add full route source + layer (dimmed backdrop)
      if (!map.getSource('mini-replay-full')) {
        map.addSource('mini-replay-full', { type: 'geojson', data: fullSegments });
      } else {
        map.getSource('mini-replay-full').setData(fullSegments);
      }
      if (!map.getLayer('mini-replay-full-line')) {
        map.addLayer({
          id: 'mini-replay-full-line',
          type: 'line',
          source: 'mini-replay-full',
          paint: {
            'line-color': ['get', 'color'],
            'line-width': trackWidth,
            'line-opacity': 0.35,
          },
          layout: { 'line-cap': 'round', 'line-join': 'round' },
        });
      }

      // Direction-flow overlay: white dashes on the same full-route source,
      // animated below to march start → end and convey travel direction.
      if (!map.getLayer('mini-replay-flow-line')) {
        map.addLayer({
          id: 'mini-replay-flow-line',
          type: 'line',
          source: 'mini-replay-full',
          paint: {
            'line-color': '#ffffff',
            'line-width': flowWidthFor(trackWidth),
            'line-opacity': 0.6,
            'line-dasharray': [0, 4, 3],
          },
          layout: { 'line-cap': 'butt', 'line-join': 'round' },
        });
      }

      // Add arrows source + layer using every 5th position
      const arrowFeatures = positions
        .filter((_, i) => i % 5 === 0)
        .map((p) => ({
          type: 'Feature',
          properties: { course: p.course || 0 },
          geometry: { type: 'Point', coordinates: [p.longitude, p.latitude] },
        }));
      const arrowsGeoJSON = { type: 'FeatureCollection', features: arrowFeatures };

      if (!map.getSource('mini-replay-arrows')) {
        map.addSource('mini-replay-arrows', { type: 'geojson', data: arrowsGeoJSON });
      } else {
        map.getSource('mini-replay-arrows').setData(arrowsGeoJSON);
      }
      if (!map.getLayer('mini-replay-arrows-layer') && map.hasImage('arrow-moving')) {
        map.addLayer({
          id: 'mini-replay-arrows-layer',
          type: 'symbol',
          source: 'mini-replay-arrows',
          layout: {
            'icon-image': 'arrow-moving',
            'icon-rotate': ['get', 'course'],
            'icon-rotation-alignment': 'map',
            'icon-size': 0.4,
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
          },
        });
      }

      // Add traveled route source + layer (full opacity, starts empty)
      if (!map.getSource('mini-replay-traveled')) {
        map.addSource('mini-replay-traveled', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        });
      }
      if (!map.getLayer('mini-replay-traveled-line')) {
        map.addLayer({
          id: 'mini-replay-traveled-line',
          type: 'line',
          source: 'mini-replay-traveled',
          paint: {
            'line-color': ['get', 'color'],
            'line-width': trackWidth,
            'line-opacity': 1.0,
          },
          layout: { 'line-cap': 'round', 'line-join': 'round' },
        });
      }

      // Start marker (green "A")
      if (!startMarkerRef.current) {
        const startEl = document.createElement('div');
        startEl.style.cssText = `
          width: 20px; height: 20px; border-radius: 50%;
          background: #10b981; border: 2px solid white;
          display: flex; align-items: center; justify-content: center;
          font-size: 10px; font-weight: bold; color: white;
          box-shadow: 0 2px 6px rgba(16,185,129,0.4);
        `;
        startEl.textContent = 'A';
        startMarkerRef.current = new maplibregl.Marker({ element: startEl, anchor: 'center' })
          .setLngLat([positions[0].longitude, positions[0].latitude])
          .addTo(map);
      }

      // End marker (red "B")
      if (!endMarkerRef.current) {
        const lastPos = positions[positions.length - 1];
        const endEl = document.createElement('div');
        endEl.style.cssText = `
          width: 20px; height: 20px; border-radius: 50%;
          background: #ef4444; border: 2px solid white;
          display: flex; align-items: center; justify-content: center;
          font-size: 10px; font-weight: bold; color: white;
          box-shadow: 0 2px 6px rgba(239,68,68,0.4);
        `;
        endEl.textContent = 'B';
        endMarkerRef.current = new maplibregl.Marker({ element: endEl, anchor: 'center' })
          .setLngLat([lastPos.longitude, lastPos.latitude])
          .addTo(map);
      }

      // Replay-position marker: a speed-colored arrow with a pulsing blue halo
      // behind it, so the playback marker is unmistakable next to live/other
      // vehicle markers (which never carry this halo).
      if (!ghostMarkerRef.current) {
        const ghostEl = document.createElement('div');
        ghostEl.style.cssText = `
          width: 28px; height: 28px; position: relative;
          filter: drop-shadow(0 2px 6px rgba(0,0,0,0.4));
        `;
        const initialColor = getSpeedColor(0, THRESHOLDS);
        ghostEl.innerHTML = `
          <span class="replay-ghost-halo" style="position:absolute;inset:-9px;border-radius:9999px;pointer-events:none;background:radial-gradient(circle, rgba(37,99,235,0.55) 0%, rgba(37,99,235,0) 70%);"></span>
          <svg viewBox="0 0 36 36" width="28" height="28" style="position:relative;">
            <path d="M18 4 L29 28 L18 22 L7 28 Z"
              fill="${initialColor}" stroke="#ffffff" stroke-width="2"/>
          </svg>`;
        const halo = ghostEl.querySelector('.replay-ghost-halo');
        if (halo && typeof halo.animate === 'function') {
          halo.animate(
            [
              { transform: 'scale(0.7)', opacity: 0.85 },
              { transform: 'scale(1.7)', opacity: 0 },
            ],
            { duration: 1500, iterations: Infinity, easing: 'ease-out' },
          );
        }
        ghostMarkerRef.current = new maplibregl.Marker({ element: ghostEl, anchor: 'center', rotationAlignment: 'map' })
          .setLngLat([positions[0].longitude, positions[0].latitude])
          .addTo(map);
      }

      // Auto-fit bounds
      let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
      for (const p of positions) {
        if (p.longitude < minLng) minLng = p.longitude;
        if (p.longitude > maxLng) maxLng = p.longitude;
        if (p.latitude < minLat) minLat = p.latitude;
        if (p.latitude > maxLat) maxLat = p.latitude;
      }
      const doFitBounds = () => {
        map.fitBounds([[minLng, minLat], [maxLng, maxLat]], { padding: 60, duration: 800 });
      };
      doFitBounds();

      // Expose fitRoute function via ref
      if (fitRouteRef) {
        fitRouteRef.current = doFitBounds;
      }

      // Direction-flow dashes (shared util — see src/lib/replayFlowDash.js).
      if (dashAnimRef.current) dashAnimRef.current();
      dashAnimRef.current = animateLineDash(map, 'mini-replay-flow-line');
    };

    // Robust style-ready wait: 'load' is one-shot (fires once per map lifetime),
    // so it never re-fires when the style is transiently not-loaded at replay
    // start (e.g. after a basemap switch) — which silently dropped the trail.
    // whenStyleReady re-checks via 'styledata'. See src/lib/mapStyleReady.js.
    const detachStyleReady = whenStyleReady(map, setup);

    return () => {
      // Detach the pending style-ready listener if we unmount before it fires.
      detachStyleReady();

      // Stop the direction-flow dash animation.
      if (dashAnimRef.current) { dashAnimRef.current(); dashAnimRef.current = null; }

      if (fitRouteRef) {
        fitRouteRef.current = null;
      }

      if (map && !map._removed) {
        // Remove layers before sources (Pitfall 3)
        ['mini-replay-traveled-line', 'mini-replay-flow-line', 'mini-replay-full-line', 'mini-replay-arrows-layer'].forEach((id) => {
          if (map.getLayer(id)) map.removeLayer(id);
        });
        ['mini-replay-traveled', 'mini-replay-full', 'mini-replay-arrows'].forEach((id) => {
          if (map.getSource(id)) map.removeSource(id);
        });
      }

      // Remove DOM markers
      if (ghostMarkerRef.current) {
        ghostMarkerRef.current.remove();
        ghostMarkerRef.current = null;
      }
      if (startMarkerRef.current) {
        startMarkerRef.current.remove();
        startMarkerRef.current = null;
      }
      if (endMarkerRef.current) {
        endMarkerRef.current.remove();
        endMarkerRef.current = null;
      }
    };
  }, [positions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect 1b: Live-update line widths when the user changes the track-width
  // preference — repaint in place instead of rebuilding the route layers.
  useEffect(() => {
    const map = mapRef.current?.getMap?.();
    if (!map) return undefined;
    const apply = () => {
      if (map.getLayer('mini-replay-full-line')) map.setPaintProperty('mini-replay-full-line', 'line-width', trackWidth);
      if (map.getLayer('mini-replay-traveled-line')) map.setPaintProperty('mini-replay-traveled-line', 'line-width', trackWidth);
      if (map.getLayer('mini-replay-flow-line')) map.setPaintProperty('mini-replay-flow-line', 'line-width', flowWidthFor(trackWidth));
    };
    const detach = whenStyleReady(map, apply);
    return () => detach();
  }, [trackWidth]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect 2: Update traveled portion on progress change
  useEffect(() => {
    const map = mapRef.current?.getMap?.();
    if (!map || !positions || positions.length < 2) return;
    if (!map.getSource('mini-replay-traveled')) return;

    const traveledIndex = Math.floor(progress * (positions.length - 1));
    const traveledPositions = positions.slice(0, traveledIndex + 1);
    const traveledSegments = buildSpeedSegments(traveledPositions, THRESHOLDS);
    map.getSource('mini-replay-traveled').setData(traveledSegments);
  }, [progress, positions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect 3: Update ghost marker position, rotation, and color on currentPosition change
  useEffect(() => {
    if (!ghostMarkerRef.current || !currentPosition) return;

    ghostMarkerRef.current.setLngLat([currentPosition.longitude, currentPosition.latitude]);
    ghostMarkerRef.current.setRotation(currentPosition.course || 0);

    // Update SVG fill color based on current speed (convert knots → km/h)
    const speedKmh = (currentPosition.speed || 0) * 1.852;
    const color = getSpeedColor(speedKmh, THRESHOLDS);
    const path = ghostMarkerRef.current.getElement()?.querySelector('path');
    if (path) path.setAttribute('fill', color);

    // Follow mode: keep the map centered on the replay marker. setCenter is
    // instant, so following stays smooth as the interpolated position glides.
    if (follow) {
      const map = mapRef?.current?.getMap?.();
      if (map) map.setCenter([currentPosition.longitude, currentPosition.latitude]);
    }
  }, [currentPosition, follow, mapRef]);

  return null;
}
